/**
 * Shared display rules for audit values.
 *
 * WHY THIS EXISTS
 *
 * Audit values are read on two surfaces — Admin -> Audit History and the
 * per-record trail panel each module embeds. Both render the same rows out of
 * `audit_logs`, so a display rule applied to one of them is a rule the other
 * one contradicts.
 *
 * Some stored values are large: an audit record's `findings` reaches 4,651
 * characters and a permit's `safety_checklist` 4,483 in live data. Rendered in
 * full they push the actual change off the screen, so each value is capped and
 * the reader is told how much was withheld rather than being shown a silent
 * truncation.
 *
 * This caps DISPLAY only. Blob and secret removal is a separate, stronger
 * boundary applied at write time in services/audit_logging.py
 * (`redact_for_audit`), so nothing sensitive reaches either surface to begin
 * with. SOC 2 CC6.7, CC7.2.
 */

/** Characters of a single audit value shown before the cap applies. */
export const AUDIT_VALUE_LIMIT = 1500;

/**
 * Cap one already-stringified audit value.
 *
 * @param {string} text  the value, already converted to a string
 * @param {string} hint  optional recourse for the full value, e.g. the CSV
 *                       export on the admin screen. Omitted where the surface
 *                       offers none — promising an export that isn't there is
 *                       worse than saying nothing.
 * @returns {string} the value, or a capped value stating what was withheld
 */
export const capAuditText = (text, hint = '') => {
  const s = typeof text === 'string' ? text : String(text ?? '');
  if (s.length <= AUDIT_VALUE_LIMIT) return s;
  const withheld = (s.length - AUDIT_VALUE_LIMIT).toLocaleString();
  const suffix = hint ? ` (${hint})` : '';
  return `${s.slice(0, AUDIT_VALUE_LIMIT)}\n… ${withheld} more characters${suffix}`;
};

/** A bare UUID, which is what a stored foreign key looks like on screen. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The display name for a stored id, or null if there isn't one.
 *
 * An audit row stores foreign keys, so without this the trail reads
 * "Department Id: 9f3c1e70-… -> 4a1b8d02-…". The names come from the API's
 * `reference_names` map, resolved on read inside the caller's own tenant.
 *
 * Returns null — never a guess — when the id is not in the map: it may belong
 * to a deleted record or another tenant, and in an audit trail showing the raw
 * id is correct where a name cannot be established.
 *
 * @param {*} value       the stored value
 * @param {Object} names  id -> name, from the API response
 * @returns {string|null}
 */
export const labelForId = (value, names) => {
  if (!names || typeof value !== 'string') return null;
  const id = value.trim();
  if (!UUID_RE.test(id)) return null;
  // Postgres renders a uuid lowercase, but a stored payload may not have. Try
  // the value as-is first, then normalised, so case never decides whether a
  // reader sees "Engineering" or a raw id.
  const label = names[id] ?? names[id.toLowerCase()];
  return label ? String(label) : null;
};
